import { ActionTable, PermissionCheck } from '@/components';
import { ProcurementApi } from '@/state/api';
import { ProcurementType } from '@/types';
import { permissionsKeys } from '@/types/permissionsType';
import { isPurchaseOrderInvoiceReadOnly } from '@/utils/purchaseOrderLifecycle';
import { Box, Button, Center, Flex, Presence, Text } from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { OverlayDialog, toasterApi } from '@ui';
import dayjs from 'dayjs';
import React, { Suspense, useState } from 'react';
import { FaArrowLeftLong } from 'react-icons/fa6';
import InvoicesManageCard from '../InvoicesManageCard';

type InvoicesProps = {
  onClose: () => void;
  purchaseOrder?: {
    id: number;
    project_po_number: string | null;
    po_number: string;
    title: string | null;
    function_number: string | null;
    status: string;
  };
};

const ViewAttachments = React.lazy(() => import('@ui/view-attachment'));

const Invoices_Inner = ({ onClose, purchaseOrder }: InvoicesProps) => {
  const po_id = purchaseOrder?.id;
  const isReadOnly = isPurchaseOrderInvoiceReadOnly(
    purchaseOrder?.status ?? '',
  );
  const [openInvoiceDetailsModal, setOpenInvoiceDetailsModal] = useState<{
    po_id: number;
    invoice_id: number;
  } | null>(null);

  const {
    data: getPurchaseOrderInvoicesAllQuery,
    isLoading: isLoadingPurchaseOrderInvoicesAllQuery,
  } = ProcurementApi.Api.useGetPurchaseOrderInvoicesAllQuery(
    po_id
      ? {
          params: {
            po_id,
          },
        }
      : skipToken,
  );

  const { data: getPurchaseOrderInvoiceQuery } =
    ProcurementApi.Api.useGetPurchaseOrderInvoiceQuery(
      openInvoiceDetailsModal
        ? {
            params: {
              po_id: openInvoiceDetailsModal?.po_id,
              invoice_id: openInvoiceDetailsModal?.invoice_id,
            },
          }
        : skipToken,
    );

  const [deletePurchaseOrderInvoiceMutation] =
    ProcurementApi.Api.useDeletePurchaseOrderInvoiceMutation();

  const handleAddInvoice = ({ po_id }: { po_id: number }) => {
    OverlayDialog.open('addInvoice', {
      overlayId: 'addInvoice',
      title: 'Add Invoice',
      content: (
        <InvoicesManageCard
          isNew
          po_id={po_id}
          onClose={() => OverlayDialog.close('addInvoice')}
        />
      ),
    });
  };
  const handleUpdateInvoice = ({
    po_id,
    invoice_id,
  }: {
    po_id: number;
    invoice_id: number;
  }) => {
    OverlayDialog.open('updateInvoice', {
      overlayId: 'updateInvoice',
      title: 'Update Invoice',
      content: (
        <InvoicesManageCard
          po_id={po_id}
          invoice_id={invoice_id}
          onClose={() => OverlayDialog.close('updateInvoice')}
        />
      ),
    });
  };

  const handleDeleteInvoice = ({
    po_id,
    invoice_id,
  }: {
    po_id: number;
    invoice_id: number;
  }) => {
    OverlayDialog.open('deleteInvoice', {
      overlayId: 'deleteInvoice',
      title: 'Delete Invoice',
      description:
        'Are you sure you want to delete this invoice? This action is irreversible.',
      handleConfirm: () => {
        deletePurchaseOrderInvoiceMutation({
          params: {
            po_id,
            invoice_id,
          },
        })
          .unwrap()
          .then(() => {
            toasterApi({
              type: 'success',
              status: 204,
              code: 'Success',
              message: 'Invoice deleted successfully',
            });
          })
          .catch(() => {
            toasterApi({
              type: 'error',
              status: 500,
              code: 'Error',
              message: 'Could not delete invoice',
            });
          });
      },
    });
  };

  const renderCell = (
    props: ActionTable.RenderCellType<ActionTableItemType, { actions: null }>,
  ) => {
    const { colKey, cellValue, rowValue } = props;

    const item = rowValue;
    switch (colKey) {
      case 'due_date':
      case 'invoice_date':
        return (
          <Text>
            {dayjs(cellValue).isValid()
              ? dayjs(cellValue).format('ddd, DD MMM YYYY')
              : 'N/A'}
          </Text>
        );
      case 'uploaded_at':
        return (
          <Text>
            {dayjs(cellValue).isValid()
              ? dayjs(cellValue).format('ddd, DD MMM YYYY, hh:mm a')
              : 'N/A'}
          </Text>
        );
      case 'uploaded_by':
        return <Text>{item?.uploaded_by?.display_name}</Text>;
      case 'actions': {
        return (
          <Flex gapX={2}>
            {!isReadOnly && (
              <PermissionCheck
                permissions={[permissionsKeys.change_purchaseorder]}
              >
                <Button
                  onClick={() =>
                    handleUpdateInvoice({ po_id: po_id!, invoice_id: item?.id })
                  }
                  variant="outline"
                  color="fg"
                  borderColor="fg"
                >
                  Edit
                </Button>
              </PermissionCheck>
            )}

            <Button
              disabled={!po_id || !item}
              onClick={() => {
                if (!po_id || !item) return;
                setOpenInvoiceDetailsModal({
                  po_id,
                  invoice_id: item?.id,
                });
              }}
            >
              View
            </Button>

            {!isReadOnly && (
              <PermissionCheck
                permissions={[permissionsKeys.delete_purchaseorder]}
              >
                <Button
                  colorPalette="red"
                  disabled={!po_id || !item}
                  onClick={() => {
                    if (!po_id || !item) return;
                    handleDeleteInvoice({
                      po_id,
                      invoice_id: item?.id,
                    });
                  }}
                >
                  Delete
                </Button>
              </PermissionCheck>
            )}
          </Flex>
        );
      }
      default:
        return cellValue?.value ?? cellValue ?? '';
    }
  };

  const filterConfigComponents: ActionTable.FilterConfigComponentType<ActionTableItemType>[] =
    [
      {
        filterComponent: 'input',
        filterComponentLabel: 'Purchase Order ID',
        filterConfigItems: [
          {
            colKeys: ['po'],
            value: '',
            operator: 'contains',
          },
        ],
      },
    ];

  return (
    <Flex flexDirection="column" gapY={4}>
      <Presence
        present={openInvoiceDetailsModal === null}
        animationName={{ _open: 'fade-in', _closed: 'fade-out' }}
        animationDuration="faster"
      >
        <Center>
          <Box maxWidth="fit-content">
            <Text fontWeight="bold" fontSize="xl" textAlign="center">
              Invoices —{' '}
              {purchaseOrder?.project_po_number ??
                purchaseOrder?.po_number ??
                'Purchase Order'}
            </Text>
            <Flex
              justifyContent="center"
              gapX={4}
              flexWrap="wrap"
              fontSize="sm"
              color="fg.subtle"
              mb={3}
            >
              {purchaseOrder?.title && <Text>{purchaseOrder.title}</Text>}
              {purchaseOrder?.function_number && (
                <Text>Function: {purchaseOrder.function_number}</Text>
              )}
              {isReadOnly && (
                <Text>Cancelled purchase order invoices are read-only.</Text>
              )}
            </Flex>
            <Flex justifyContent="space-between" alignItems="center">
              <Button size="sm" bg="fg" color="fg.inverted" onClick={onClose}>
                <FaArrowLeftLong /> Purchase Order
              </Button>

              {!isReadOnly && (
                <PermissionCheck
                  permissions={[permissionsKeys.add_purchaseorder]}
                >
                  <Button
                    size="sm"
                    onClick={() => handleAddInvoice({ po_id: po_id! })}
                  >
                    Add Invoice
                  </Button>
                </PermissionCheck>
              )}
            </Flex>
            <Flex flexDirection="column" gapY={2}>
              <ActionTable.FilterBar
                filterConfigComponents={filterConfigComponents}
              />
              <Box display="flex" flexDirection="row" gap={5}>
                <Box h="100%" my={5}>
                  <ActionTable<ActionTableItemType, { actions: null }>
                    width="75vw"
                    columns={columns}
                    columnToUseAsRowKey="id"
                    items={getPurchaseOrderInvoicesAllQuery ?? []}
                    isPaginated={false}
                    isPaginatedAsync={false}
                    totalItemsCount={
                      getPurchaseOrderInvoicesAllQuery?.length ?? 0
                    }
                    isSortable={true}
                    isFilterable={true}
                    isShowActionBar={false}
                    height={'55vh'}
                    rowHeight={70}
                    isLoading={isLoadingPurchaseOrderInvoicesAllQuery}
                    isMultipleSelection={false}
                    isCheckbox={false}
                    isSelectionDisabled={true}
                    renderCell={renderCell}
                  />
                </Box>
              </Box>
            </Flex>
          </Box>
        </Center>
      </Presence>

      <Presence
        present={openInvoiceDetailsModal !== null}
        animationName={{ _open: 'fade-in', _closed: 'fade-out' }}
        animationDuration="faster"
      >
        <Flex flexDirection="column" gapY={4} my={5}>
          <Text fontWeight="bold" fontSize="xl" textAlign="center">
            Invoice Details
          </Text>
          <Flex justifyContent="space-between" alignItems="center">
            <Button
              size="sm"
              bg="fg"
              color="fg.inverted"
              onClick={() => setOpenInvoiceDetailsModal(null)}
            >
              <FaArrowLeftLong /> Invoices
            </Button>
            <Flex gapX={2}>
              {!isReadOnly && (
                <PermissionCheck
                  permissions={[permissionsKeys.change_purchaseorder]}
                >
                  <Button
                    size="sm"
                    disabled={!po_id || !getPurchaseOrderInvoiceQuery?.id}
                    onClick={() => {
                      if (!po_id || !getPurchaseOrderInvoiceQuery?.id) return;
                      handleUpdateInvoice({
                        po_id,
                        invoice_id: getPurchaseOrderInvoiceQuery?.id,
                      });
                    }}
                    variant="outline"
                    color="fg"
                    borderColor="fg"
                  >
                    Edit
                  </Button>
                </PermissionCheck>
              )}

              {!isReadOnly && (
                <PermissionCheck
                  permissions={[permissionsKeys.delete_purchaseorder]}
                >
                  <Button
                    size="sm"
                    colorPalette="red"
                    disabled={!po_id || !getPurchaseOrderInvoiceQuery?.id}
                    onClick={() => {
                      if (!po_id || !getPurchaseOrderInvoiceQuery?.id) return;
                      handleDeleteInvoice({
                        po_id,
                        invoice_id: getPurchaseOrderInvoiceQuery?.id,
                      });
                      setOpenInvoiceDetailsModal(null);
                    }}
                  >
                    Delete
                  </Button>
                </PermissionCheck>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex flexDirection="column" gapY={2}>
          <Text>
            <strong>Invoice Number:&nbsp;</strong>
            {getPurchaseOrderInvoiceQuery?.invoice_number}
          </Text>
          <Text>
            <strong>Invoice Date:&nbsp;</strong>
            {getPurchaseOrderInvoiceQuery?.invoice_date &&
              dayjs(getPurchaseOrderInvoiceQuery?.invoice_date).format(
                'ddd, DD MMMM YYYY',
              )}
          </Text>
          <Text>
            <strong>Due Date:&nbsp;</strong>
            {getPurchaseOrderInvoiceQuery?.due_date &&
            dayjs(getPurchaseOrderInvoiceQuery.due_date).isValid()
              ? dayjs(getPurchaseOrderInvoiceQuery?.due_date).format(
                  'ddd, DD MMMM YYYY',
                )
              : 'N/A'}
          </Text>
          <Text>
            <strong>Purchase Order:&nbsp;</strong>
            {purchaseOrder?.project_po_number ??
              purchaseOrder?.po_number ??
              getPurchaseOrderInvoiceQuery?.po ??
              'N/A'}
          </Text>
          <Text>
            <strong>Subtotal Amount:&nbsp;</strong>
            {getPurchaseOrderInvoiceQuery?.subtotal_amount}
          </Text>
          <Text>
            <strong>Freight Amount:&nbsp;</strong>
            {getPurchaseOrderInvoiceQuery?.freight_amount ?? 'N/A'}
          </Text>
          <Text>
            <strong>Tax Amount:&nbsp;</strong>
            {getPurchaseOrderInvoiceQuery?.tax_amount ?? 'N/A'}
          </Text>
          <Text>
            <strong>Total Amount:&nbsp;</strong>
            {getPurchaseOrderInvoiceQuery?.total_amount}
          </Text>
          <Text>
            <strong>Notes:&nbsp;</strong>
            {getPurchaseOrderInvoiceQuery?.notes ?? 'N/A'}
          </Text>
          <Flex marginTop={4}>
            <Suspense fallback={<Text>Loading attachments...</Text>}>
              <ViewAttachments
                attachments={[
                  {
                    id: getPurchaseOrderInvoiceQuery?.id ?? 0,
                    filename: getPurchaseOrderInvoiceQuery?.filename ?? '',
                    file: getPurchaseOrderInvoiceQuery?.file_url ?? '',
                  },
                ]}
              />
            </Suspense>
          </Flex>
        </Flex>
      </Presence>
    </Flex>
  );
};

const Invoices = ({ onClose, purchaseOrder }: InvoicesProps) => {
  return (
    <ActionTable.Provider>
      <Invoices_Inner onClose={onClose} purchaseOrder={purchaseOrder} />
    </ActionTable.Provider>
  );
};
export default Invoices;

const columns: ActionTable.ColumnsConfigType<
  ActionTableItemType,
  { actions: null }
> = [
  {
    id: {
      label: 'Id',
      width: '50px',
    },
  },
  {
    invoice_number: {
      label: 'Invoice Number',
      width: '100px',
    },
  },
  {
    invoice_date: {
      label: 'Invoice Date',
      width: '100px',
    },
  },
  {
    due_date: {
      label: 'Due Date',
      width: '100px',
    },
  },
  {
    po: {
      label: 'PO',
      width: '50px',
    },
  },
  {
    uploaded_by: {
      label: 'Uploaded By',
      width: '100px',
    },
  },
  {
    subtotal_amount: {
      label: 'Subtotal Amount',
      width: '100px',
    },
  },
  {
    freight_amount: {
      label: 'Freight Amount',
      width: '100px',
    },
  },
  {
    tax_amount: {
      label: 'Tax Amount',
      width: '100px',
    },
  },
  {
    total_amount: {
      label: 'Total Amount',
      width: '100px',
    },
  },
  {
    notes: {
      label: 'Notes',
      width: '100px',
    },
  },
  {
    actions: {
      label: 'Actions',
      width: '250px',
    },
  },
];

export type ActionTableItemType =
  ProcurementType.PurchaseOrders.GetPurchaseOrderInvoicesAll['response'][number];
